import { http } from './request'

export interface WorkbenchConversationSession {
  sessionId: string
  tenantId?: number
  ownerUserId?: number
  productCode: string
  sourceType: string
  clientInstanceId?: string
  threadId?: string
  deviceId?: string | null
  robotId?: number | null
  status: string
  connectionId?: string | null
  incarnationId?: string | null
  expiresAt?: string | null
  closedAt?: string | null
  closeReason?: string | null
}

interface OpenCommand {
  openRequestId: string
  clientInstanceId: string
  sourceType: 'WORKBENCH'
  transport: 'WEBSOCKET'
  threadId: string
  deviceId?: string
  robotId?: number
  externalSessionId?: string
  previousSessionId?: string
}

interface SessionLookup {
  sourceType: 'WORKBENCH'
  threadId: string
  externalSessionId?: string
  robotId?: number
}

interface CloseCommand {
  closeRequestId: string
  connectionId: string
  reason: 'USER_CLOSE' | 'LOGOUT'
}

const TERMINAL_STATUSES = new Set(['CLOSED', 'EXPIRED', 'FAILED'])

function assertResponse<T>(response: { code: number; data: T; message?: string }): T {
  if (response.code !== 200) {
    throw new Error(response.message || '统一会话服务暂不可用')
  }
  return response.data
}

export function isTerminalWorkbenchSession(session: WorkbenchConversationSession | null | undefined): boolean {
  return Boolean(session && TERMINAL_STATUSES.has(session.status))
}

export async function resolveWorkbenchConversation(lookup: SessionLookup): Promise<WorkbenchConversationSession | null> {
  const response = await http.post<WorkbenchConversationSession | null>('/conversation-sessions/resolve', lookup)
  return assertResponse(response)
}

export async function openWorkbenchConversation(command: OpenCommand): Promise<WorkbenchConversationSession> {
  const response = await http.post<WorkbenchConversationSession>('/conversation-sessions', command)
  return assertResponse(response)
}

export async function closeWorkbenchConversation(
  session: WorkbenchConversationSession,
  reason: CloseCommand['reason'] = 'USER_CLOSE',
): Promise<WorkbenchConversationSession> {
  if (!session.sessionId || !session.connectionId) {
    throw new Error('统一会话连接信息缺失，无法安全关闭会话')
  }
  const response = await http.post<WorkbenchConversationSession>(
    `/conversation-sessions/${encodeURIComponent(session.sessionId)}/close`,
    {
      closeRequestId: crypto.randomUUID(),
      connectionId: session.connectionId,
      reason,
    },
  )
  return assertResponse(response)
}

export function createWorkbenchClientInstanceId(): string {
  const key = 'afsa.workbench.client-instance-id'
  const existing = window.localStorage.getItem(key)
  if (existing) return existing
  const created = globalThis.crypto.randomUUID()
  window.localStorage.setItem(key, created)
  return created
}

export function createWorkbenchThreadId(userId?: string | number): string {
  const key = `afsa.workbench.thread-id:${userId ?? 'anonymous'}`
  const existing = window.localStorage.getItem(key)
  if (existing) return existing
  const created = globalThis.crypto.randomUUID()
  window.localStorage.setItem(key, created)
  return created
}
